import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import useHorizontalScroll from "@/hooks/useHorizontalScroll";
import useAmbientAudio from "@/hooks/useAmbientAudio";
import useGalleryData from "@/hooks/useGalleryData";
import GrainOverlay from "@/components/GrainOverlay";
import CustomCursor from "@/components/CustomCursor";
import AudioToggle from "@/components/AudioToggle";
import TopNav from "@/components/TopNav";
import ViewToggle from "@/components/ViewToggle";
import FavouritesWidget from "@/components/FavouritesWidget";
import IntroPanel from "@/components/IntroPanel";
import AboutPanel from "@/components/AboutPanel";
import ManifestoChapter from "@/components/ManifestoChapter";
import ArtworkSection from "@/components/ArtworkSection";
import ContactSection from "@/components/ContactSection";

export default function HomeGallery() {
  useHorizontalScroll();
  const audio = useAmbientAudio();
  const navigate = useNavigate();
  const { gallery, paintings } = useGalleryData();

  useEffect(() => {
    window.__ARTIST_EMAIL__ = gallery.artist_email;
  }, [gallery.artist_email]);

  const canCustomCursor = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const chapters = gallery.chapters || [];
  const total = paintings.length;
  const onOpen = (p) => navigate(`/paintings/${p.slug}`, { state: { from: "hall" } });
  const work = (p, i) => (
    <ArtworkSection key={p.id || i} painting={p} index={i} total={total} onOpen={onOpen} />
  );

  return (
    <div className="gallery-root" data-testid="gallery-root">
      <GrainOverlay />
      {canCustomCursor && <CustomCursor />}
      <div className="scroll-progress" data-testid="scroll-progress" />
      <TopNav gallery={gallery} />
      <ViewToggle current="hall" />
      <FavouritesWidget />
      <AudioToggle enabled={audio.enabled} onToggle={audio.toggle} />

      <IntroPanel gallery={gallery} />
      <AboutPanel gallery={gallery} />

      <div className="hscroll-zone" aria-label="Gallery walk-through">
        <div className="hscroll-sticky">
          <main className="hall" data-testid="hall">
            <span className="hall__floor" aria-hidden="true" />

            {chapters[0] && (
              <ManifestoChapter {...chapters[0]} />
            )}
            {paintings.slice(0, 7).map((p, i) => work(p, i))}

            {chapters[1] && (
              <ManifestoChapter {...chapters[1]} />
            )}
            {paintings.slice(7, 14).map((p, i) => work(p, i + 7))}

            {chapters[2] && (
              <ManifestoChapter {...chapters[2]} />
            )}
            {paintings.slice(14).map((p, i) => work(p, i + 14))}
          </main>
        </div>
      </div>

      <ContactSection gallery={gallery} />
    </div>
  );
}
